import { AlertCircle } from '@tamagui/lucide-icons';
import {
  SensorType,
  useSensors,
  useLeakDetectorData,
  extractReadingValueFromSensorEvent,
  LeakDetectorReading,
  useSensorAlerts,
  SensorAlertType,
} from 'common';
import { format, subDays } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { BarChart, barDataItem } from 'react-gifted-charts';
import { useTranslation } from 'react-i18next';
import { Text, View, XStack, YStack } from 'tamagui';
import { tokens } from 'tamagui-config';

import { GlowCircle } from '../../../components/common/GlowCircle';
import { useAppSync } from '../../../contexts/AppSyncContext';
import { useLatestSensorEvents } from '../../../hooks/sensors/useLatestSensorEvent';
import { useGetLeakDetectionData } from '../../../hooks/water/useGetLeakDetectionData';
import { isDateStale } from '../../../utils/dateUtils';

const CHART_WIDTH = 548;
const CHART_HEIGHT = 140;
const BAR_MARGIN = 5;

const getSubtitleColor = (leakDetected: boolean) => {
  if (leakDetected) {
    return tokens.color.warningRed;
  } else {
    return tokens.color.white;
  }
};

export const LeakDetectedContent: React.FC = () => {
  const { selectedHubId } = useAppSync();
  const { t } = useTranslation();
  const { sensors } = useSensors(selectedHubId ?? undefined);
  const [chartWidth] = useState(CHART_WIDTH);

  const leakDetectorSensorId = useMemo(() => {
    const leakSensor = sensors.find((sensor) => sensor.type === SensorType.LEAK_DETECTOR);
    return leakSensor?.id;
  }, [sensors]);

  const { data: leakDetectorRollupData } = useLeakDetectorData({
    sensorId: leakDetectorSensorId,
    hubId: selectedHubId ?? undefined,
    days: 1,
    enabled: !!leakDetectorSensorId,
  });

  const { event: leakDetectorEvent } = useLatestSensorEvents(SensorType.LEAK_DETECTOR);
  const latestHumidity = leakDetectorEvent
    ? extractReadingValueFromSensorEvent<LeakDetectorReading>(leakDetectorEvent)?.humidity
    : null;
  const { leakDetected: currentLeakStatus, gradientColors, statusText } = useGetLeakDetectionData();

  const { sensorAlerts } = useSensorAlerts(leakDetectorSensorId);

  const hasActiveLeakAlertInLastHour = useMemo(() => {
    if (!sensorAlerts || sensorAlerts.length === 0) return false;

    const nowUTC = Date.now(); // Current time in UTC milliseconds
    const oneHourAgoUTC = nowUTC - 60 * 60 * 1000; // One hour ago in UTC milliseconds

    return sensorAlerts.some((alert) => {
      // Only include LEAK alerts as leak-related alerts, not humidity alerts
      const isLeakRelatedAlert = alert.alertType.includes(SensorAlertType.Leak);

      if (!alert.timestamp) return false;
      const alertTimestampUTC = alert.timestamp * 1000; // Convert to UTC milliseconds
      const isWithinLastHour = alertTimestampUTC >= oneHourAgoUTC;

      return isLeakRelatedAlert && isWithinLastHour;
    });
  }, [sensorAlerts]);

  // Check if data is stale (last reported is more than 24 hours ago)
  const lastReported = leakDetectorEvent?.timestamp
    ? format(new Date(leakDetectorEvent.timestamp * 1000), 'MM/dd, h:mm a')
    : undefined;

  const isDataStale = useMemo(() => isDateStale(lastReported), [lastReported]);

  // Check if the last reported leak was within the last hour
  const isLastReportedWithinHour = useMemo(() => {
    if (!leakDetectorEvent?.timestamp) {
      return false;
    }

    const sensorReading =
      extractReadingValueFromSensorEvent<LeakDetectorReading>(leakDetectorEvent);
    const hasLeakInEvent = (sensorReading?.leak ?? 0) > 0;

    if (!hasLeakInEvent) {
      return false;
    }

    const alertDate = new Date(leakDetectorEvent.timestamp * 1000);
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    return alertDate >= oneHourAgo;
  }, [leakDetectorEvent?.timestamp]);

  // Convert leak detector rollup data to leak point format for the chart
  const processedLeakData = useMemo(() => {
    // If data is stale or empty, generate placeholder data with zeros
    if (isDataStale || !leakDetectorRollupData || leakDetectorRollupData.length === 0) {
      // Calculate PST time
      const now = new Date();
      const pstOffset = -8; // hours from UTC
      const localOffset = now.getTimezoneOffset() / 60;
      const timezoneDiff = pstOffset + localOffset;
      const pstNow = new Date(now.getTime() + timezoneDiff * 60 * 60 * 1000);

      // Start time is exactly 24 hours ago from current PST time
      const startTime = new Date(pstNow.getTime() - 24 * 60 * 60 * 1000);

      // Create placeholder data for 24 hours
      const emptyData: Array<{
        value: number;
        time: string;
        color: string;
        leakDetected: boolean;
      }> = [];
      const hoursBetween = 24;

      for (let i = 0; i < hoursBetween; i++) {
        // Create a new date for each hour by adding i hours to the start time
        const hourDate = new Date(startTime.getTime() + i * 60 * 60 * 1000);

        emptyData.push({
          value: 0,
          time: format(hourDate, 'h:mm a'),
          color: tokens.color.gray8, // Always gray for stale data
          leakDetected: false,
        });
      }

      return emptyData;
    }

    // Process normal data if it's available and not stale
    const recentData = leakDetectorRollupData.slice(0, 24);

    const chartData = recentData.map((hourData, index) => {
      const hourDate = new Date(hourData.label);
      hourDate.setHours(hourData.hour ?? 0, 0, 0, 0);

      const isMostRecentHour = index === recentData.length - 1;

      const humidityValue =
        isMostRecentHour && latestHumidity !== null && latestHumidity !== undefined
          ? latestHumidity
          : (hourData.humidity ?? 0);

      const rollupHasLeak = (hourData.leakCount ?? 0) > 0;

      // For the last bar: show leak if there's an active leak alert in last hour OR rollup data shows leak OR sensor detected leak within the hour
      // For other bars: show leak based on historical rollup data only
      const hasLeak = isMostRecentHour
        ? hasActiveLeakAlertInLastHour || rollupHasLeak || isLastReportedWithinHour
        : rollupHasLeak;

      let color = tokens.color.white7;

      if (hasLeak) {
        color = tokens.color.energyRed;
      } else if (humidityValue >= 61) {
        color = tokens.color.energyRed;
      } else if (humidityValue >= 51) {
        color = tokens.color.warning;
      }

      const result = {
        value: Math.max(humidityValue, 0.1),
        time: format(hourDate, 'h:mm a'),
        color,
        leakDetected: hasLeak,
      };
      return result;
    });

    return chartData;
  }, [leakDetectorRollupData, latestHumidity, currentLeakStatus, isLastReportedWithinHour]);

  const spacing = 6;
  const horizontalPadding = 24;
  const chartAreaWidth = chartWidth - horizontalPadding;

  const barCount = processedLeakData.length;
  const totalSpacing = (barCount - 1) * spacing;
  const barWidth = (chartAreaWidth - totalSpacing) / barCount;
  const barBorderRadius = 8;

  const getDefaultLabels = () => {
    const now = new Date();
    const yesterday = subDays(now, 1);
    yesterday.setHours(18, 0, 0, 0);

    const currentHour = new Date(now);
    const middleTime = new Date(yesterday);
    middleTime.setHours(yesterday.getHours() + 12);

    return {
      first: format(yesterday, 'h:mm a'),
      middle: format(middleTime, 'h:mm a'),
      last: format(currentHour, 'h:mm a'),
    };
  };

  const defaultLabels = getDefaultLabels();
  const firstLabel = processedLeakData[0]?.time || defaultLabels.first;
  const middleLabel = processedLeakData[Math.floor(barCount / 2)]?.time || defaultLabels.middle;
  // If data is stale, show "Waiting for New Data" as the last label
  const lastLabel = processedLeakData[barCount - 1]?.time || defaultLabels.last;

  const subtitleColor = getSubtitleColor(currentLeakStatus);

  const chartData: barDataItem[] = processedLeakData.map((item) => ({
    value: (item.value ?? 0) + BAR_MARGIN,
    frontColor: item.color,
    spacing,
  }));

  // If data is stale, use gray colors for the glow
  const safeGradientColors = Array.isArray(gradientColors) ? gradientColors : [];
  const [first, second, third] = isDataStale
    ? ['#444444', '#333333', '#222222']
    : [
        safeGradientColors[0] ?? tokens.color.hgPrimaryGradientStart,
        safeGradientColors[1] ?? tokens.color.hgPrimaryGradientEnd,
        safeGradientColors[2] ?? tokens.color.hgPrimaryGradientEnd,
      ];

  return (
    <YStack gap="$2.5">
      {/* Header with leak detection title and optional time - matching mobile BaseCard structure */}
      <XStack alignItems="center" justifyContent="space-between" width="100%">
        <XStack alignItems="center" gap="$2">
          <Text
            fontFamily="$heading"
            fontWeight="$9.5"
            fontSize="$9.5"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            color={subtitleColor}>
            {isDataStale ? t('water.waitingForNewData') : statusText}
          </Text>
          {currentLeakStatus && <AlertCircle size="$2.5" color={tokens.color.warningRed} />}
        </XStack>
      </XStack>

      <YStack gap="$1.5">
        <View position="relative">
          <Gradients colors={[first, second, third]} />

          <BarChart
            data={chartData.map(({ value, frontColor }) => ({
              value,
              frontColor,
            }))}
            width={CHART_WIDTH}
            height={CHART_HEIGHT}
            maxValue={100 + BAR_MARGIN}
            spacing={spacing}
            barWidth={barWidth}
            initialSpacing={2}
            endSpacing={0}
            xAxisThickness={0}
            yAxisThickness={0}
            yAxisColor="transparent"
            hideRules
            hideYAxisText
            xAxisLabelsHeight={0}
            barBorderTopLeftRadius={barBorderRadius}
            barBorderTopRightRadius={barBorderRadius}
            isAnimated={false}
            disableScroll={true}
          />
        </View>

        {/* Time Labels */}
        <XStack justifyContent="space-between" paddingHorizontal="$3" marginTop="$2">
          <Text fontSize="$4" color="$textLight">
            {firstLabel}
          </Text>
          <Text fontSize="$4" color="$textLight" fontWeight="700">
            {middleLabel}
          </Text>
          <Text fontSize="$4" color="$textLight">
            {lastLabel}
          </Text>
        </XStack>
      </YStack>
    </YStack>
  );
};

const Gradients = ({ colors }: { colors: [string, string, string] }) => (
  <YStack
    style={{
      position: 'absolute',
      top: 0,
      left: -25,
      width: 600,
      height: 290,
      pointerEvents: 'none',
    }}>
    <GlowCircle
      size={360}
      color={colors[0]}
      opacity={0.2}
      style={{ position: 'absolute', left: 0, top: 0 }}
    />
    <GlowCircle
      size={300}
      color={colors[1]}
      opacity={0.2}
      style={{ position: 'absolute', left: 300, top: 0 }}
    />
    <GlowCircle
      size={380}
      color={colors[2]}
      opacity={0.2}
      style={{ position: 'absolute', left: 110, top: 82 }}
    />
  </YStack>
);

export default LeakDetectedContent;
